import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ComplianceCase, Inspection, InspectionFinding, InspectionOutcome } from '@regigov/contracts';
import { casesApi, inspectionsApi } from '../api/compliance';
import { Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

const OUTCOME_LABEL: Record<InspectionOutcome, string> = { compliant: 'Compliant', non_compliant: 'Non-compliant' };
/** Today in Sydney time, the calendar the API checks inspection dates against. */
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

function target(i: Inspection): string {
  if (i.installationNmi) return `NMI ${i.installationNmi} · ${i.unitSerial ?? i.unitId}`;
  if (i.unitId) return `Unit ${i.unitSerial ?? i.unitId}`;
  return i.subjectParticipantName;
}

function useRefresh(caseId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['case', caseId] });
    void qc.invalidateQueries({ queryKey: ['cases'] });
    void qc.invalidateQueries({ queryKey: ['units'] });
    void qc.invalidateQueries({ queryKey: ['inspections'] });
  };
}

function Findings({ findings }: { findings: InspectionFinding[] }) {
  if (findings.length === 0) return <span className="muted">—</span>;
  return (
    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
      {findings.map((f, n) => (
        <li key={n}>
          <code>{f.item}</code> = {f.observed} <StatusBadge status={f.compliant ? 'compliant' : 'non_compliant'} />
          {f.note && <div className="muted">{f.note}</div>}
        </li>
      ))}
    </ul>
  );
}

function Result({ i }: { i: Inspection }) {
  if (i.status === 'cancelled') return <span className="muted">Cancelled{i.cancelledAt ? ` ${formatDateTime(i.cancelledAt)}` : ''}: {i.cancelReason}</span>;
  if (i.status !== 'completed' || !i.outcome) return <span className="muted">Not yet inspected</span>;
  return (
    <>
      <StatusBadge status={i.outcome} /> inspected {i.inspectedOn}
      <div style={{ whiteSpace: 'pre-wrap' }}>{i.outcomeSummary}</div>
      {i.evidenceRef && (
        <div>
          Evidence: <code>{i.evidenceRef}</code>
        </div>
      )}
    </>
  );
}

/** Inspections on a case (C4): the list for everyone, plus CER scheduling and recording while the case is open. */
export function InspectionPanel({ c, mode }: { c: ComplianceCase; mode: 'cer' | 'participant' }) {
  const editable = mode === 'cer' && c.status !== 'closed';
  return (
    <>
      <h2>Inspections</h2>
      {c.inspections.length === 0 ? (
        <p className="muted">No inspections.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Inspection</th>
              <th>Of</th>
              <th>Scheduled for</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Findings</th>
              <th>Outcome</th>
              {mode === 'cer' && <th>By</th>}
            </tr>
          </thead>
          <tbody>
            {c.inspections.map((i) => (
              <tr key={i.id}>
                <td>
                  <code>{i.id}</code>
                </td>
                <td>
                  {i.installationId ? (
                    <Link to={`${mode === 'cer' ? '/cer/register' : '/participant/devices'}/installations/${i.installationId}`}>{target(i)}</Link>
                  ) : i.unitId ? (
                    <Link to={`${mode === 'cer' ? '/cer/register' : '/participant/devices'}/units/${i.unitId}`}>{target(i)}</Link>
                  ) : (
                    target(i)
                  )}
                </td>
                <td>{i.scheduledFor}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{i.reason}</td>
                <td>
                  <StatusBadge status={i.status} />
                </td>
                <td>
                  <Findings findings={i.findings} />
                </td>
                <td>
                  <Result i={i} />
                </td>
                {mode === 'cer' && (
                  <td>
                    {i.scheduledByName}
                    {i.completedByName && <div className="muted">Recorded by {i.completedByName}</div>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editable && (
        <>
          {c.inspections
            .filter((i) => i.status === 'scheduled')
            .map((i) => (
              <RecordInspection key={i.id} i={i} caseId={c.id} />
            ))}
          <ScheduleInspection c={c} />
        </>
      )}
    </>
  );
}

function ScheduleInspection({ c }: { c: ComplianceCase }) {
  const refresh = useRefresh(c.id);
  const sites = useQuery({ queryKey: ['cases', 'subject-installations', c.subjectParticipantId], queryFn: () => casesApi.subjectInstallations(c.subjectParticipantId) });
  const units = useQuery({ queryKey: ['cases', 'subject-units', c.subjectParticipantId], queryFn: () => casesApi.subjectUnits(c.subjectParticipantId) });
  const [what, setWhat] = useState('');
  const [scheduledFor, setScheduledFor] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const schedule = useMutation({
    mutationFn: () => {
      const [kind, id] = what.split(':');
      return inspectionsApi.schedule({ caseId: c.id, scheduledFor, reason, installationId: kind === 'i' ? id : undefined, unitId: kind === 'u' ? id : undefined });
    },
    onSuccess: () => (setWhat(''), setReason(''), refresh()),
  });
  return (
    <form className="form card" onSubmit={(e) => (e.preventDefault(), schedule.mutate())}>
      <h2>Schedule an inspection</h2>
      <Field label="Inspect" hint={`Installations and units of ${c.subjectParticipantName}, or the participant as a whole (e.g. a records review).`}>
        <select value={what} onChange={(e) => setWhat(e.target.value)} disabled={schedule.isPending}>
          <option value="">{c.subjectParticipantName} (participant)</option>
          {sites.data && sites.data.items.length > 0 && (
            <optgroup label="Installations">
              {sites.data.items.map((s) => (
                <option key={s.id} value={`i:${s.id}`}>
                  NMI {s.nmi} · {s.unitSerial} ({s.status}, commissioned {s.commissionedOn})
                </option>
              ))}
            </optgroup>
          )}
          {units.data && units.data.items.length > 0 && (
            <optgroup label="Units">
              {units.data.items.map((u) => (
                <option key={u.id} value={`u:${u.id}`}>
                  {u.serial} ({u.relations.join(', ')})
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </Field>
      <Field label="Scheduled for">
        <input type="date" value={scheduledFor} min={todayLocal()} onChange={(e) => setScheduledFor(e.target.value)} required disabled={schedule.isPending} />
      </Field>
      <Field label="Reason">
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} required disabled={schedule.isPending} />
      </Field>
      <p className="muted">Linked to this case and recorded on its timeline, visible to {c.subjectParticipantName}.</p>
      <ProblemAlert error={sites.error ?? units.error ?? schedule.error} />
      <div>
        <button type="submit" disabled={schedule.isPending || reason.trim().length < 3 || !scheduledFor}>
          Schedule inspection
        </button>
      </div>
    </form>
  );
}

type Draft = { item: string; observed: string; compliant: boolean; note: string };
const blank = (): Draft => ({ item: '', observed: '', compliant: true, note: '' });

function RecordInspection({ i, caseId }: { i: Inspection; caseId: string }) {
  const refresh = useRefresh(caseId);
  const [inspectedOn, setInspectedOn] = useState(todayLocal());
  const [rows, setRows] = useState<Draft[]>([blank()]);
  const [evidenceRef, setEvidenceRef] = useState('');
  const [outcome, setOutcome] = useState<InspectionOutcome>('non_compliant');
  const [summary, setSummary] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const set = (n: number, patch: Partial<Draft>) => setRows(rows.map((r, k) => (k === n ? { ...r, ...patch } : r)));
  const findings = rows.filter((r) => r.item.trim() && r.observed.trim()).map((r) => ({ item: r.item.trim(), observed: r.observed.trim(), compliant: r.compliant, ...(r.note.trim() ? { note: r.note.trim() } : {}) }));
  const complete = useMutation({ mutationFn: () => inspectionsApi.complete(i.id, { inspectedOn, findings, evidenceRef: evidenceRef.trim() || undefined, outcome, summary }), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => inspectionsApi.cancel(i.id, { reason: cancelReason }), onSuccess: refresh });
  const busy = complete.isPending || cancel.isPending;
  return (
    <>
      <form className="form card" onSubmit={(e) => (e.preventDefault(), complete.mutate())}>
        <h2>
          Record inspection <code>{i.id}</code>
        </h2>
        <p className="muted">
          {target(i)}, scheduled for {i.scheduledFor}. Once recorded the inspection is final.
        </p>
        <Field label="Inspected on">
          <input type="date" value={inspectedOn} max={todayLocal()} onChange={(e) => setInspectedOn(e.target.value)} required disabled={busy} />
        </Field>
        <table className="table">
          <thead>
            <tr>
              <th>Item checked</th>
              <th>Observed</th>
              <th>Compliant</th>
              <th>Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, n) => (
              <tr key={n}>
                <td>
                  <input aria-label="Item checked" value={r.item} onChange={(e) => set(n, { item: e.target.value })} placeholder="grid_connected" disabled={busy} />
                </td>
                <td>
                  <input aria-label="Observed" value={r.observed} onChange={(e) => set(n, { observed: e.target.value })} placeholder="false" disabled={busy} />
                </td>
                <td>
                  <input type="checkbox" aria-label="Compliant" checked={r.compliant} onChange={(e) => set(n, { compliant: e.target.checked })} disabled={busy} />
                </td>
                <td>
                  <input aria-label="Note" value={r.note} onChange={(e) => set(n, { note: e.target.value })} disabled={busy} />
                </td>
                <td>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => setRows(rows.filter((_, k) => k !== n))} disabled={busy}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <button type="button" onClick={() => setRows([...rows, blank()])} disabled={busy}>
            Add finding
          </button>
        </div>
        <Field label="Evidence reference" hint="Photo set, report ID or URL of the evidence held by CER.">
          <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Outcome">
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as InspectionOutcome)} disabled={busy}>
            {Object.entries(OUTCOME_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Summary">
          <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} required disabled={busy} />
        </Field>
        <ProblemAlert error={complete.error} />
        <div>
          <button type="submit" disabled={busy || findings.length === 0 || summary.trim().length < 3}>
            Record inspection
          </button>
        </div>
      </form>
      <form className="form card" onSubmit={(e) => (e.preventDefault(), cancel.mutate())}>
        <h2>
          Cancel inspection <code>{i.id}</code>
        </h2>
        <Field label="Reason">
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} required disabled={busy} />
        </Field>
        <ProblemAlert error={cancel.error} />
        <div>
          <button type="submit" disabled={busy || cancelReason.trim().length < 3}>
            Cancel inspection
          </button>
        </div>
      </form>
    </>
  );
}

/** The participant's inspections across its cases, with outcomes, on its Compliance page. */
export function InspectionOutcomes({ basePath }: { basePath: string }) {
  const list = useQuery({ queryKey: ['inspections'], queryFn: () => inspectionsApi.list(), staleTime: 0, gcTime: 0 });
  return (
    <>
      <h2>Inspections</h2>
      <p className="muted">Inspections CER has scheduled or carried out on your organisation, its installations and units, with their findings and outcome.</p>
      <ProblemAlert error={list.error} />
      {list.data &&
        (list.data.items.length === 0 ? (
          <p>No inspections.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Inspection</th>
                <th>Case</th>
                <th>Of</th>
                <th>Scheduled for</th>
                <th>Status</th>
                <th>Findings</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <code>{i.id}</code>
                  </td>
                  <td>
                    <Link to={`${basePath}/${i.caseId}`}>{i.caseTitle}</Link>
                  </td>
                  <td>{target(i)}</td>
                  <td>{i.scheduledFor}</td>
                  <td>
                    <StatusBadge status={i.status} />
                  </td>
                  <td>
                    <Findings findings={i.findings} />
                  </td>
                  <td>
                    <Result i={i} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </>
  );
}
